/**
 * XP-P203A BLE GATT sends TSPL in small ATT writes. Pack whole TSPL lines / BITMAP
 * commands when possible so UART bridges deliver complete commands.
 */

import { parseBinaryBitmapAt } from './tsplEncode';

const TSPL_CMD_RE = /\b(SIZE|GAP|CLS|PRINT|TEXT|BITMAP|QRCODE|BARCODE|DIRECTION|REFERENCE|SPEED|DENSITY)\b/i;

/** True when payload looks like a TSPL/TSPL2 ASCII job (not ESC/POS binary). */
export function isLikelyTsplPayload(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  if (bytes[0] === 0x1b || bytes[0] === 0x1d) return false;
  try {
    const sample = bytes.subarray(0, Math.min(512, bytes.length));
    const text = new TextDecoder('utf-8', { fatal: false }).decode(sample);
    const head = text.trimStart().toUpperCase();
    if (head.startsWith('SIZE ') || head.startsWith('CLS')) return true;
    return TSPL_CMD_RE.test(text);
  } catch {
    return false;
  }
}

export type TsplBleChunk = {
  bytes: Uint8Array;
  /** True when this ATT write completes a TSPL command (text line or full BITMAP). */
  endsLine: boolean;
};

type TsplUnit = { start: number; end: number };

function findCrlfLineEnd(data: Uint8Array, from: number): number {
  for (let i = from; i < data.length - 1; i += 1) {
    if (data[i] === 0x0d && data[i + 1] === 0x0a) return i + 2;
  }
  return data.length;
}

/** Split TSPL job into atomic units: text lines or complete binary BITMAP commands. */
export function tokenizeTsplPayload(data: Uint8Array): TsplUnit[] {
  const units: TsplUnit[] = [];
  let pos = 0;

  while (pos < data.length) {
    const bitmap = parseBinaryBitmapAt(data, pos);
    if (bitmap) {
      units.push({ start: pos, end: bitmap.end });
      pos = bitmap.end;
      continue;
    }

    const end = findCrlfLineEnd(data, pos);
    if (end <= pos) break;
    units.push({ start: pos, end });
    pos = end;
  }

  return units;
}

/**
 * Split TSPL bytes into BLE write chunks. Never splits inside a text line or binary
 * BITMAP payload except when a single unit exceeds maxChunkBytes (burst sub-chunks).
 */
export function splitTsplPayloadForBleChunks(data: Uint8Array, maxChunkBytes: number): TsplBleChunk[] {
  if (maxChunkBytes < 1 || data.length <= maxChunkBytes) {
    return [{ bytes: data.slice(), endsLine: true }];
  }

  const units = tokenizeTsplPayload(data);
  if (units.length === 0) {
    return [{ bytes: data.slice(), endsLine: true }];
  }

  const chunks: TsplBleChunk[] = [];
  let packStart = -1;
  let packEnd = -1;

  const flushPack = (): void => {
    if (packStart >= 0) {
      chunks.push({ bytes: data.slice(packStart, packEnd), endsLine: true });
      packStart = -1;
      packEnd = -1;
    }
  };

  const pushSubChunks = (start: number, end: number): void => {
    let off = start;
    while (off < end) {
      const chunkEnd = Math.min(off + maxChunkBytes, end);
      chunks.push({
        bytes: data.slice(off, chunkEnd),
        endsLine: chunkEnd >= end,
      });
      off = chunkEnd;
    }
  };

  for (const { start, end } of units) {
    const unitLen = end - start;
    if (unitLen > maxChunkBytes) {
      flushPack();
      pushSubChunks(start, end);
      continue;
    }

    if (packStart < 0) {
      packStart = start;
      packEnd = end;
    } else if (packEnd - packStart + unitLen <= maxChunkBytes) {
      packEnd = end;
    } else {
      flushPack();
      packStart = start;
      packEnd = end;
    }
  }

  flushPack();
  return chunks.length > 0 ? chunks : [{ bytes: data.slice(), endsLine: true }];
}

/** Legacy helper: byte arrays only (for tests that join chunks). */
export function splitTsplPayloadForBleChunkBytes(data: Uint8Array, maxChunkBytes: number): Uint8Array[] {
  return splitTsplPayloadForBleChunks(data, maxChunkBytes).map((c) => c.bytes);
}
